import { loadStripe, Stripe } from '@stripe/stripe-js';
import config from 'config';
import Logger from 'utils/logger';
import { isProductionMode } from 'utils/helpers';

/**
 * Why Stripe.js is not available, once a load has been attempted:
 *
 * - `not_configured` — the build has no `REACT_APP_STRIPE_PUBLISHABLE_KEY`.
 *   Runtime env injection is disabled (`utils/env.ts`), so this is permanent
 *   for the bundle: nobody can pay, 3DS cannot run and the return handler
 *   cannot read an intent. The production build step refuses to build
 *   without the key, so seeing this in prod means that guard was bypassed.
 * - `load_failed` — `js.stripe.com` did not load (network, blocker). Not
 *   cached, so the next call retries.
 */
export type StripeJsUnavailableReason = 'not_configured' | 'load_failed';

let stripePromise: Promise<Stripe | null> | null = null;
let unavailableReason: StripeJsUnavailableReason | null = null;

/**
 * Loads Stripe.js once and shares it. Resolves `null` when Stripe is not
 * available; every caller must turn that into a visible error (they render
 * `billing.errors.processorUnavailable`), and the cause is reported to Sentry
 * here as an Error so a misconfigured build pages someone instead of quietly
 * disabling payments. {@link getStripeJsUnavailableReason} says which cause.
 */
export function getStripeJs(): Promise<Stripe | null> {
  if (stripePromise) return stripePromise;

  const key = config.STRIPE_PUBLISHABLE_KEY;

  if (!key) {
    unavailableReason = 'not_configured';
    Logger.error(
      new Error(
        'getStripeJs: REACT_APP_STRIPE_PUBLISHABLE_KEY is not configured — card payments are disabled',
      ),
    );
    stripePromise = Promise.resolve(null);
    return stripePromise;
  }

  if (isProductionMode() && key.startsWith('pk_test_')) {
    // Loud but not fatal: the build guard should have refused this bundle.
    Logger.error(
      new Error('getStripeJs: production build uses a Stripe TEST key'),
    );
  }

  unavailableReason = null;
  const attempt = loadStripe(key)
    .then((stripe) => {
      if (!stripe) {
        unavailableReason = 'load_failed';
        Logger.error(new Error('getStripeJs: loadStripe resolved null'));
        stripePromise = null;
      }
      return stripe;
    })
    .catch((error) => {
      unavailableReason = 'load_failed';
      Logger.error(new Error('getStripeJs: loadStripe failed'), {
        error: String(error),
      });
      // Do not cache a transient failure: the next caller retries.
      stripePromise = null;
      return null;
    });

  stripePromise = attempt;
  return attempt;
}

export function getStripeJsUnavailableReason(): StripeJsUnavailableReason | null {
  return unavailableReason;
}

export function __resetStripeJsForTests(): void {
  stripePromise = null;
  unavailableReason = null;
}
